// Đồng bộ thẻ liệu trình với đơn POS: phát hành thẻ khi bán gói, ghi nhận tiền thu / hoàn trên đơn vào sổ thẻ.
// Hàm thuần — backend thật sẽ làm việc này trong cùng transaction tạo đơn / thu nợ / hoàn tiền.
import { staffName, type Customer, type Order, type PricedLine, type Refund as PosRefund, type Transaction } from '../POS/data';
import { policyOf, type CardRefund, type CardRefundStatus, type Installment, type TreatmentCard, type TreatmentConfig, type TreatmentPackage } from './data';
import { addDaysYmd, buildLines, issueValidity, ymd } from './logic';

export interface PosSnapshot { orders: Order[]; transactions: Transaction[]; refunds: PosRefund[]; customers: Customer[] }

const fmt = (n: number) => new Intl.NumberFormat('vi-VN').format(Math.round(n));

/** Tỷ lệ của dòng gói trong tổng đơn — dùng chia tiền thu / hoàn khi đơn có cả hàng khác */
export const lineShare = (order: Order, line: PricedLine) => (order.totals.total > 0 ? line.net / order.totals.total : 0);

/** Lịch mặc định khi bán tại POS: phần đã thu là đợt 1, phần nợ là đợt 2 sau N ngày */
export function defaultPlan(price: number, paidNow: number, saleDate: string, intervalDays: number): Installment[] {
  const first = Math.min(price, Math.max(0, paidNow));
  if (first >= price) return [{ no: 1, dueDate: saleDate, amount: price }];
  if (first <= 0) return [{ no: 1, dueDate: addDaysYmd(saleDate, intervalDays), amount: price }];
  return [{ no: 1, dueDate: saleDate, amount: first }, { no: 2, dueDate: addDaysYmd(saleDate, intervalDays), amount: price - first }];
}

export function cardFromOrder(
  id: string, order: Order, line: PricedLine, pkg: TreatmentPackage, customer: Pick<Customer, 'id' | 'name' | 'phone'>,
  opts: { plan: Installment[]; consultant?: string; by: string },
): TreatmentCard {
  const qty = Math.max(1, line.qty);
  const scaled = { items: pkg.items.map(i => ({ ...i, sessions: i.sessions * qty })) };
  const price = line.net;
  const at = order.createdAt;
  return {
    id, customerId: customer.id, customerName: customer.name, customerPhone: customer.phone,
    packageId: pkg.id, packageCode: pkg.code, packageName: pkg.name, category: pkg.category, kind: pkg.kind,
    branchId: order.branchId, soldBy: order.cashier, consultant: opts.consultant || staffName(line.staffId) || '',
    createdAt: at, ...issueValidity(pkg, ymd(at)),
    listPrice: line.gross, price,
    lines: pkg.kind === 'session' ? buildLines(scaled, price) : [],
    faceValue: pkg.kind === 'value' ? pkg.faceValue * qty : 0, valueAdjust: 0, valueUsedBefore: 0,
    allowedServiceIds: pkg.kind === 'value' ? pkg.items.map(i => i.serviceId) : [],
    installments: opts.plan, payments: [], usages: [], bookings: [], refunds: [],
    events: [{ at, by: opts.by, kind: 'issue', text: `Phát hành thẻ từ đơn ${order.id} – giá ${fmt(price)}đ, ${opts.plan.length} đợt thanh toán` }],
    status: 'active', source: 'pos', orderId: order.id, lineKey: line.key, note: '', policy: policyOf(pkg),
  };
}

const mapPosRefundStatus = (s: PosRefund['status']): CardRefundStatus => (s === 'paid' ? 'paid' : s === 'rejected' ? 'rejected' : 'pending');

export interface SyncContext {
  packages: TreatmentPackage[];
  cfg: TreatmentConfig;
  /** Chỉ tự phát hành thẻ cho đơn tạo từ thời điểm này (đơn cũ trước khi bật module thì bỏ qua) */
  since: string;
  nextId: (kind: 'card' | 'pay' | 'refund') => string;
}

/** Trả về danh sách thẻ mới (null nếu không có gì thay đổi) và các thẻ vừa phát hành */
export function reconcile(cards: TreatmentCard[], snap: PosSnapshot, ctx: SyncContext): { cards: TreatmentCard[] | null; issued: TreatmentCard[] } {
  let changed = false;
  const issued: TreatmentCard[] = [];
  const linked = new Set(cards.filter(c => c.orderId).map(c => `${c.orderId}|${c.lineKey}`));

  // 1. Phát hành thẻ cho dòng gói trên đơn POS mới
  snap.orders.forEach(order => {
    if (order.createdAt < ctx.since || order.status === 'cancelled' || !order.customerId) return;
    order.lines.forEach(line => {
      if (line.type !== 'package' || linked.has(`${order.id}|${line.key}`)) return;
      const pkg = ctx.packages.find(p => p.id === line.itemId);
      if (!pkg) return;
      const customer = snap.customers.find(c => c.id === order.customerId) || { id: order.customerId!, name: order.customerName, phone: order.customerPhone };
      const paidNow = Math.round(order.paid * lineShare(order, line));
      const plan = defaultPlan(line.net, paidNow, ymd(order.createdAt), pkg.pricing?.intervalDays ?? ctx.cfg.defaultIntervalDays);
      issued.push(cardFromOrder(ctx.nextId('card'), order, line, pkg, customer, { plan, by: order.cashier }));
      linked.add(`${order.id}|${line.key}`);
    });
  });

  // 2. Ghi nhận tiền thu / hoàn / hủy đơn vào thẻ đã liên kết
  const all = [...cards, ...issued];
  const next = all.map(card => {
    if (!card.orderId) return card;
    const order = snap.orders.find(o => o.id === card.orderId);
    const line = order?.lines.find(l => l.key === card.lineKey);
    if (!order || !line) return card;
    const share = lineShare(order, line);
    let c = card;
    const touch = () => { if (c === card) c = { ...card, payments: [...card.payments], refunds: [...card.refunds], events: [...card.events] }; changed = true; };

    snap.transactions
      .filter(t => t.orderId === order.id && t.kind !== 'refund' && !card.payments.some(p => p.posTxnId === t.id))
      .sort((a, b) => a.at.localeCompare(b.at))
      .forEach(t => {
        const amount = share >= 0.9999 ? t.amount : Math.round(t.amount * share);
        if (amount <= 0) return;
        touch();
        c.payments.push({ id: ctx.nextId('pay'), at: t.at, amount, method: t.method, txnRef: t.txnRef, by: t.cashier, branchId: t.branchId, posTxnId: t.id });
        c.events.push({ at: t.at, by: t.cashier, kind: 'payment', text: `${t.kind === 'payment' ? 'Thu tiền bán thẻ' : 'Thu tiền đợt'} qua POS (${t.id})`, amount });
      });

    snap.refunds.filter(r => r.orderId === order.id).forEach(r => {
      const rl = r.lines.find(l => l.lineKey === card.lineKey);
      if (!rl) return;
      const status = mapPosRefundStatus(r.status);
      const idx = c.refunds.findIndex(x => x.posRefundId === r.id);
      if (idx >= 0) {
        const cur = c.refunds[idx];
        if (cur.status === status) return;
        touch();
        c.refunds[idx] = { ...cur, status, approvedBy: r.approvedBy, approvedAt: r.approvedAt || r.paidAt, rejectReason: r.rejectReason };
        c.events.push({ at: r.paidAt || r.approvedAt || new Date().toISOString(), by: r.approvedBy || r.createdBy, kind: 'refund', text: status === 'paid' ? `Đã chi hoàn tiền (${r.id})` : `Phiếu hoàn ${r.id} bị từ chối${r.rejectReason ? `: ${r.rejectReason}` : ''}`, amount: status === 'paid' ? -cur.amount : undefined });
      } else {
        touch();
        const refund: CardRefund = {
          id: ctx.nextId('refund'), at: r.createdAt, by: r.createdBy, mode: rl.qty >= line.qty ? 'full' : 'partial', sessions: [], valueReturned: 0,
          amount: rl.amount, fee: 0, method: r.method, txnRef: r.txnRef, reason: r.reason, status, posRefundId: r.id,
          approvedBy: r.approvedBy, approvedAt: r.approvedAt, rejectReason: r.rejectReason,
        };
        c.refunds.push(refund);
        c.events.push({ at: r.createdAt, by: r.createdBy, kind: 'refund', text: `Hoàn tiền từ POS (${r.id}) – ${status === 'paid' ? 'đã chi' : status === 'pending' ? 'chờ duyệt' : 'từ chối'}`, amount: status === 'paid' ? -rl.amount : undefined });
      }
      const fullPaid = c.refunds.some(x => x.mode === 'full' && x.status === 'paid');
      if (fullPaid && c.status === 'active') { touch(); c.status = 'refunded'; }
    });

    if (order.status === 'cancelled' && c.status === 'active') {
      touch();
      c.status = 'cancelled';
      c.events.push({ at: new Date().toISOString(), by: 'Hệ thống', kind: 'issue', text: `Hủy thẻ do đơn ${order.id} bị hủy${order.cancelReason ? `: ${order.cancelReason}` : ''}` });
    }
    return c;
  });

  if (issued.length) changed = true;
  return { cards: changed ? next : null, issued: issued.map(i => next.find(c => c.id === i.id)!) };
}
